import React, { useState, useEffect, useRef} from "react";
import axios from "axios";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DateTime } from "luxon";
import Select from "react-select";
import countries from "./countries"; 
import { useParams, useNavigate } from "react-router-dom"; // Import useNavigate
import CONFIG from "./config";
import { useTranslation } from 'react-i18next';

const PublicPage = () => {
    const { business_id } = useParams();
    const navigate = useNavigate(); // Hook for navigation
    const { t } = useTranslation(['publicPage', 'common']);
    const [businessData, setBusinessData] = useState(null);
    const [availability, setAvailability] = useState({}); // Stores raw availability with slots
    const [availableDates, setAvailableDates] = useState([]); // Stores only the dates for calendar
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sentVerificationCode, setSentVerificationCode] = useState(false);
    const [enteredCode, setEnteredCode] = useState("");
    const [services, setServices] = useState([]);
    const [customerInfo, setCustomerInfo] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
        streetAddress: "",
        additionalInfo: "",
    });    
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [redirectError, setRedirectError] = useState("");
    const [redirectSuccess, setRedirectSuccess] = useState("");
    const [reqCusInfo, setReqCusInfo] = useState({ name: true, email: true, phone: false, address: false });
    const bookingSectionRef = React.useRef(null);
    const [isChecked, setIsChecked] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [businessTimezone, setBusinessTimezone] = useState("");
    const [customerTimezone, setCustomerTimezone] = useState(window.Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [timezone, setTimezone] = useState(customerTimezone);
    const calendarRef = useRef(null);
    const serviceRef = useRef(null);


    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + CONFIG.siteKey;
        script.async = true;
        document.body.appendChild(script);
        return () => { document.body.removeChild(script); };
    }, []);

    useEffect(() => {
        fetchBusinessData();
        fetchServices();
        fetchAvailability(30, null);
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const errorParam = queryParams.get("error");
        const successParam = queryParams.get("success");
      
        if (errorParam) {
          setRedirectError(errorParam);
        }
        if (successParam) {
          setRedirectSuccess(successParam);
        }
        // Remove the query parameters from the URL without reloading the page
        const newUrl = window.location.pathname;
        window.history.replaceState(null, "", newUrl);
      }, []);      

    
    useEffect(() => {
        const initializeServiceAndAvailability = async () => {
            // Fetch business data
            const servicesData = await fetchServices();
    
            if (servicesData && servicesData.length > 0) {
                // Find the service with the smallest duration
                const smallestDurationService = servicesData.reduce((minService, currentService) => {
                    return currentService.duration < minService.duration ? currentService : minService;
                });
    
                setSelectedService(smallestDurationService); // Set the initial service
    
                // Fetch availability for the smallest duration
                await fetchAvailability(smallestDurationService.duration,smallestDurationService.service_id);
            } else {
                // Default fetch if no services are available
                await fetchAvailability(30,null);
            }
        };
    
        initializeServiceAndAvailability();
    }, []); // Empty dependency array ensures this runs only once on load
    
    
    useEffect(() => {
        if (availableSlots.length > 0 && !selectedDate) {
            // Set the first available slot as default selected date
            setSelectedDate(DateTime.fromISO(availableSlots[0], { zone: timezone || "UTC" }).toJSDate());
        }
    }, [availableSlots, selectedDate]); // Run when availableSlots changes

    // Modified fetchBusinessData to return data
    const fetchBusinessData = async () => {
        try {
            const response = await axios.get(`${CONFIG.BACKEND_URL}/api/public/${business_id}`);
            const fetchedData = response.data;
    
            setBusinessData(fetchedData);
            setBusinessTimezone(fetchedData.timezone);
    
            // Initialize required customer info state based on fetched data
            setReqCusInfo(fetchedData.req_cus_info || { name: true, email: true, phone: false, address: false });
    
            return fetchedData; // Return fetched data for immediate use
        } catch (err) {
            console.error("❌ Error fetching business data:", err);
            // Redirect if business is not found (404) or not published
            if (err.response && err.response.status === 404) {
                navigate("/"); // Redirect to login page
            } else {
                setError(t('publicPage:fetchBusinessDataFailed'));
            }
        }
    };
    
    const fetchServices = async () => {
        const response = await axios.get(`${CONFIG.BACKEND_URL}/api/services_public/${business_id}`);
        setServices(response.data.services || []);
        return response.data.services || [];
    };
    
    const fetchAvailability = async (duration = 30, serviceId = null) => {
        if (!serviceId) {
            setError("Please select a service first");
            return { rawAvailability: {}, availableDates: [] };
        }

        try {
            const response = await axios.get(
                `${CONFIG.BACKEND_URL}/api/availability/${business_id}`,
                { 
                    params: { 
                        duration,
                        service_id: serviceId
                    }
                }
            );

            const rawAvailability = response.data.availability || {};
            const businessTimezone = timezone || "UTC";

            // Extract available dates for calendar view
            const availableDates = Object.keys(rawAvailability).filter(
                (date) => rawAvailability[date].length > 0
            );

            setAvailability(rawAvailability);
            setAvailableDates(availableDates.map((date) => 
                DateTime.fromISO(date, { zone: businessTimezone }).toISODate()
            ));

            return { rawAvailability, availableDates };
        } catch (err) {
            console.error("Error fetching availability:", err);
            setError(t('publicPage:fetchAvailabilityFailed'));
            return { rawAvailability: {}, availableDates: [] };
        }
    };

    const handleDateClick = async (info) => {
        setError("");
        const businessTimezone = timezone || "UTC";
    
        const clickedDate = DateTime.fromFormat(info.dateStr, "yyyy-MM-dd", { zone: businessTimezone }).toISODate();
    
        try {
            const duration = selectedService?.duration || 30;
            const { rawAvailability } = await fetchAvailability(duration, selectedService.service_id);
    
    
            const rawSlots = rawAvailability[clickedDate] || [];
    
            const convertedSlots = rawSlots.map((utcSlot) =>
                DateTime.fromISO(utcSlot, { zone: "UTC" }).setZone(businessTimezone).toISO()
            );

            // ✅ Properly handle empty slots before setting selectedDate
            if (convertedSlots.length > 0 && convertedSlots[0]) {
                setSelectedDate(DateTime.fromISO(convertedSlots[0], { zone: businessTimezone }).toJSDate());
            } else {
                setSelectedDate(DateTime.fromFormat(clickedDate, "yyyy-MM-dd", { zone: businessTimezone }).toJSDate());
            }
            
            setAvailableSlots(convertedSlots);

            // Defer scrolling until DOM updates
            setTimeout(() => {
                if (bookingSectionRef.current) {
                bookingSectionRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }, 0);

            // ✅ Auto-scroll to the booking section
            if (bookingSectionRef.current) {
                bookingSectionRef.current.scrollIntoView({ behavior: "smooth" });
            }
        } catch (err) {
            console.error("❌ Error fetching slots for date click:", err);
            setError(t('publicPage:fetchSlotsFailed'));
        }
    };
    
    const handleServiceChange = async (service) => {
        setSelectedService(service);
        setError("");
        setAvailableSlots([]); // Clear existing slots when service changes

        if (service) {
            try {
                const { rawAvailability } = await fetchAvailability(
                    service.duration,
                    service.service_id
                );

                if (selectedDate) {
                    const businessTimezone = timezone || "UTC";
                    const dateKey = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toISODate();
                    const updatedSlots = (rawAvailability[dateKey] || []).map(slot =>
                        DateTime.fromISO(slot, { zone: "UTC" }).setZone(businessTimezone).toISO()
                    );
                    setAvailableSlots(updatedSlots);
                    console.log("Updated Slots:", updatedSlots);
                }
            } catch (err) {
                console.error("Error updating availability:", err);
                setError(t('publicPage:updateAvailabilityFailed'));
            }
        }
    };
    
    

    const handleBookingSubmit = async () => {
        // Clear any existing error or success messages
        setError("");
        setSuccessMessage("");

        if (!enteredCode) {
            setError(t('publicPage:enterVerificationCode'));
            return;
          }

        if (!isChecked) {
            setError(t('publicPage:mustAgreeTerms'));
            return;
          }

        if (!selectedService || !selectedDate) {
            setError(t('publicPage:selectServiceAndTime'));
            return;
        }

        // Email validation regex
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (businessData?.req_cus_info?.email && !emailPattern.test(customerInfo.email)) {
            setError(t('publicPage:validEmail'));
            return;
        }
    
        const requiredFields = {
            name: businessData?.req_cus_info?.name || false,
            email: businessData?.req_cus_info?.email || false,
            phone: businessData?.req_cus_info?.phone || false,
            address: businessData?.req_cus_info?.address || false,
        };
        
    
        // Validate required fields
        if (
            (requiredFields.name && !customerInfo.name) ||
            (requiredFields.email && !customerInfo.email) ||
            (requiredFields.phone && !customerInfo.phone) ||
            (requiredFields.address &&
                (!customerInfo.streetAddress ||
                    !customerInfo.city ||
                    !customerInfo.state ||
                    !customerInfo.country ||
                    !customerInfo.zipCode))
        ) {
            setError(t('publicPage:fillRequiredFields'));
            return;
        }

        // ✅ Prevent multiple submissions
        if (isSubmitting) return;
        setIsSubmitting(true); // Disable the button
    
        // Merge the full address into a single formatted string if required
        let fullAddress = "";
        if (requiredFields.address) {
            fullAddress = `${customerInfo.streetAddress}, ${customerInfo.city}, ${customerInfo.state}, ${customerInfo.country}, ${customerInfo.zipCode}`;
        }
    
        try {
            const businessTimezone = timezone || "UTC";
            // ✅ Convert selected date from business timezone to UTC
            const startDateTimeUTC = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toUTC().toISO();
            console.log("Start DateTime UTC:", startDateTimeUTC);
            const endDateTimeUTC = DateTime.fromJSDate(
                new Date(selectedDate.getTime() + selectedService.duration * 60000),
                { zone: businessTimezone }
            ).toUTC().toISO();
    
            // Check capacity before booking
            const availabilityResponse = await axios.get(
                `${CONFIG.BACKEND_URL}/api/availability/${business_id}`,
                { 
                    params: { 
                        duration: selectedService.duration,
                        service_id: selectedService.service_id
                    }
                }
            );
    
            const updatedAvailability = availabilityResponse.data.availability || {};
            const selectedDateKey = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toUTC().toISODate();
            const updatedSlots = updatedAvailability[selectedDateKey] || [];
    
            if (!updatedSlots.includes(startDateTimeUTC)) {
                setError(t('publicPage:timeSlotUnavailable'));
                return;
            }
    
            // Proceed with booking
            const bookingData = {
                name: requiredFields.name ? customerInfo.name : "",
                email: requiredFields.email ? customerInfo.email : "",
                phone: requiredFields.phone ? customerInfo.phone : "",
                address: requiredFields.address ? fullAddress : "",
                service: selectedService,
                timeSlot: startDateTimeUTC, // Converted to UTC
                endTime: endDateTimeUTC, // Converted to UTC
                additionalInfo: customerInfo.additionalInfo || "",
                verificationCode: enteredCode,
                service_id: selectedService.service_id,
                num_of_customer: 1
            };

            const token = await window.grecaptcha.enterprise.execute(CONFIG.siteKey, { action: 'submit' });
            
            const response = await axios.post(`${CONFIG.BACKEND_URL}/api/public/${business_id}/book`, 
                    { ...bookingData, 
                        recaptchaToken: token,
                        action: 'submit'
                     });
            setSuccessMessage(response.data.message || t('publicPage:bookingSubmitted'));
            setError("");
            setSentVerificationCode(false);
            setEnteredCode("");
        } catch (err) {
            console.error("Error during booking:", err);
            setError(t('publicPage:bookingFailed'));
        } finally {
            setIsSubmitting(false); // ✅ Re-enable the button
        }
    };
    
    // Step 1: Send code
    const handleSendCode = async () => {
        setError("");
        setSuccessMessage("");
        setIsButtonDisabled(true); // Disable button
    
        if (!isChecked) {
            setError(t('publicPage:mustAgreeTerms'));
            setIsButtonDisabled(false); // Enable button
            return;
          }

        if (!selectedService || !selectedDate) {
            setError(t('publicPage:selectServiceAndTime'));
            setIsButtonDisabled(false); // Enable button
            return;
        }

        // Email validation regex
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (businessData?.req_cus_info?.email && !emailPattern.test(customerInfo.email)) {
            setError(t('publicPage:validEmail'));
            setIsButtonDisabled(false); // Enable button
            return;
        }
    
        const requiredFields = {
            name: businessData?.req_cus_info?.name || false,
            email: businessData?.req_cus_info?.email || false,
            phone: businessData?.req_cus_info?.phone || false,
            address: businessData?.req_cus_info?.address || false,
        };
        
    
        // Validate required fields
        if (
            (requiredFields.name && !customerInfo.name) ||
            (requiredFields.email && !customerInfo.email) ||
            (requiredFields.phone && !customerInfo.phone) ||
            (requiredFields.address &&
                (!customerInfo.streetAddress ||
                    !customerInfo.city ||
                    !customerInfo.state ||
                    !customerInfo.country ||
                    !customerInfo.zipCode))
        ) {
            setError(t('publicPage:fillRequiredFields'));
            setIsButtonDisabled(false); // Enable button
            return;
        }

        
    
        try {
        const token = await window.grecaptcha.enterprise.execute(CONFIG.siteKey, { action: 'submit' });

        const businessTimezone = timezone || "UTC";
        const startDateTimeUTC = DateTime.fromJSDate(selectedDate, { zone: businessTimezone }).toUTC().toISO();
        
        await axios.post(`${CONFIG.BACKEND_URL}/api/public/${business_id}/send-code`, {
            recaptchaToken: token,
            action: 'submit',
            email: customerInfo.email,
            service: selectedService,
            timeSlot: startDateTimeUTC,
            businessName: businessData?.name || "Rili Day" // Or pass whichever name you want
        });

        setSentVerificationCode(true);
        setError("");
        setCountdown(60); // Set countdown to 60 seconds

        // Start the countdown timer
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer); // Stop timer when it reaches 0
                    setIsButtonDisabled(false); // Enable button
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    
        setSuccessMessage(t('publicPage:verificationCodeSent'));
        } catch (err) {
        console.error("Send code error:", err);
        setError(err.response?.data?.error || t('publicPage:verificationCodeFailed'));
        setIsButtonDisabled(false); // Enable button
        }
    };
    
    
    
    if (!businessData) return null; // Prevent rendering empty page before redirect

    return (
        <div>
            <div style={styles.container}>
                {redirectError && <p style={styles.error}>{redirectError}</p>}
                {redirectSuccess && <p style={styles.success}>{redirectSuccess}</p>}

                {businessData && (
                    <>
                        <h1 style={styles.title}>{businessData.name}</h1>
                        <p style={styles.description}>
                            {businessData.description}
                        </p>
                        {availableSlots && services && services.length > 0 && (
                            <button style={styles.button} onClick={() => {
                                serviceRef.current.scrollIntoView({ behavior: "smooth" });
                            }}>{t('publicPage:bookAppointment')}</button>
                        )}<br /><br />
                        
                        {businessData.business_phone && (
                        <p><strong>{t('common:phone')}:</strong> {businessData.business_phone}</p>
                        )}
                        {businessData.business_email && (
                        <p><strong>{t('common:email')}:</strong> {businessData.business_email}</p>
                        )}
                        {businessData.business_address && (
                        <p><strong>{t('common:address')}:</strong> {businessData.business_address}</p>
                        )}  
                    </>
                )}

                        {services && services.length > 0 && (
                            <>
                                <p ref={serviceRef}><strong>{t('publicPage:selectService')}</strong></p>
                                <div style={styles.servicesList}>
                                    {services.map((service, index) => (
                                        <div 
                                            key={index} 
                                            style={styles.serviceCard}
                                            onClick={() => {
                                                handleServiceChange(service);
                                                calendarRef.current.scrollIntoView({ behavior: "smooth" });
                                            }}
                                        >
                                            <div style={styles.serviceInfo}>
                                                <p style={styles.serviceName}><strong>{service.name}</strong></p>
                                                <span style={styles.serviceDuration}>{service.duration} {t('publicPage:minutes')}</span>
                                            </div>
                                            {service.description && (
                                                <p style={styles.serviceDescription}>
                                                    {service.description}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                {availableSlots && services && services.length > 0 && (
                <div style={styles.calendarSection} ref={calendarRef}>
                    <p style={styles.startPrompt}>{t('publicPage:clickCalendar')}
                        <span style={styles.colorBlock}>  
                        </span> 
                        {t('publicPage:onCalendar')}
                    </p>

                    <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        height="auto"
                        headerToolbar={{
                            left: "prev,next today",
                            center: "",
                            right: "title"
                        }}
                        titleFormat={{ month: "long" }}  // ✅ Show only the month name (e.g., "February")
                        dayHeaderFormat={{ weekday: 'narrow' }}
                        events={availableDates.map((date) => ({
                            title: "",
                            start: date, // Only date (no time)
                            display: "background",
                            //backgroundColor: "rgba(14, 111, 1, 1)", // Override global color
                            //borderColor: "transparent",
                        }))}
                        dateClick={handleDateClick}
                    />
                </div>
                )}

                {selectedDate && (
                    <div style={styles.bookingSection} ref={bookingSectionRef}>
                        {services && services.length > 0 && (
                        <>
                        <label style={styles.label}>{t('publicPage:changeTimezone')}</label>
                        <select
                            value={timezone}
                            onChange={(e) => {
                                const newTimezone = e.target.value;
                                console.log('Selected Timezone:', newTimezone);
                                setTimezone(newTimezone);
                                fetchAvailability(selectedService.duration, selectedService.service_id);
                            }}
                            style={{marginBottom: '20px'}}
                        >
                        
                            <option key={1} value={customerTimezone}>{t('publicPage:yourLocalTimezone')} {customerTimezone}</option>
                            <option key={2} value={businessTimezone}>{t('publicPage:businessTimezone')} {businessTimezone}</option>
                        
                        </select>
                        </>
                        )}
                        
                        <label style={styles.label}>
                            {t('publicPage:dateSelected')}{" "}
                            {selectedDate.toLocaleDateString([], {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </label>
                        <br />
                        <label style={styles.label}>{t('publicPage:changeService')}</label>
                        <select
                            style={styles.input}
                            value={selectedService ? selectedService.name : ""}
                            onChange={(e) => {

                                const selectedServiceName = e.target.value;
                                if (selectedServiceName) {
                                    const service = services.find(service => service.name === selectedServiceName);
                                    if (service) {
                                        setSelectedService(service);
                                        handleServiceChange(service); // Manually trigger handleServiceChange
                                    }
                                } else {
                                    console.log("⚠️ No service selected");
                                }
                            }}
                        >
                            {services.map((service, index) => (
                                <option key={index} value={service.name}>
                                    {service.name} - {service.duration} {t('publicPage:minutes')}
                                </option>
                            ))}
                        </select>

                        <label style={styles.label}>{t('publicPage:availableSlots')}</label>
                        {availableSlots.length > 0 ? (
                            <select
                                style={styles.input}
                                value={selectedDate ? DateTime.fromJSDate(selectedDate).toISO() : ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value) {
                                        // Convert selected slot to a JavaScript Date in the business timezone
                                        const selectedSlot = DateTime.fromISO(value, { zone: timezone || "UTC" }).toJSDate();
                                        
                                        setSelectedDate(selectedSlot); // Update selected date
                                    } else {
                                        console.log("⚠️ No slot selected.");
                                    }
                                }}
                            >
                                {availableSlots.map((slot, index) => (
                                    <option key={index} value={slot}>
                                        {DateTime.fromISO(slot, { zone: timezone || "UTC" }).toFormat("hh:mm a")}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <p style={styles.error}>{t('publicPage:noAvailableSlots')}</p>
                        )}
                        {reqCusInfo.name && (
                            <>
                                <label style={styles.label}>{t('common:name')}</label>
                                <input
                                    type="text"
                                    placeholder={t('common:required')}
                                    style={styles.input}
                                    value={customerInfo.name}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                                    required
                                />
                            </>
                        )}

                        {/* Email is always required */}
                                <label style={styles.label}>{t('common:email')}</label>
                                <input
                                    type="email"
                                    placeholder={t('common:required')}
                                    style={styles.input}
                                    value={customerInfo.email}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                                />

                        {reqCusInfo.phone && (
                            <>
                                <label style={styles.label}>{t('common:phone')}</label>
                                <input
                                    type="tel"
                                    placeholder={t('common:required')}
                                    style={styles.input}
                                    value={customerInfo.phone}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value.replace(/\D/g, '') })}
                                    required
                                />
                            </>
                        )}

                        {businessData?.req_cus_info?.address && (
                            <div>
                                <label style={styles.label}>{t('common:address')}</label>
                                <label style={styles.label}>{t('common:country')}</label>
                                <Select
                                    options={countries}
                                    value={countries.find(c => c.value === customerInfo.country)}
                                    onChange={(selected) => {
                                        setCustomerInfo({ ...customerInfo, country: selected.value, state: "" });
                                    }}
                                />

                                {customerInfo.country && (
                                    <>
                                        <label style={{...styles.label, marginTop: '10px'}}>{t('common:state')}</label>
                                        <Select
                                            options={countries.find(c => c.value === customerInfo.country)?.states || []}
                                            value={countries.find(c => c.value === customerInfo.country)?.states.find(s => s.value === customerInfo.state)}
                                            onChange={(selected) => setCustomerInfo({ ...customerInfo, state: selected.value })}
                                            styles={customSelectStyles}
                                        />
                                    </>
                                )}
                                <label style={{...styles.label, marginTop: '10px'}}>{t('common:city')}</label>
                                <input
                                    type="text"
                                    placeholder={t('common:required')}
                                    style={styles.input}
                                    value={customerInfo.city}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, city: e.target.value })}
                                    required
                                />

                                <label style={styles.label}>{t('common:streetAddress')}</label>
                                <input
                                    type="text"
                                    placeholder={t('common:required')}
                                    style={styles.input}
                                    value={customerInfo.streetAddress}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, streetAddress: e.target.value })}
                                    required
                                />

                                <label style={styles.label}>{t('common:zipCode')}</label>
                                <input
                                    type="text"
                                    style={styles.input}
                                    value={customerInfo.zipCode}
                                    onChange={(e) => setCustomerInfo({ ...customerInfo, zipCode: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        <label style={styles.label}>{t('common:additionalInfo')}</label>
                        <textarea
                            style={styles.input}
                            placeholder={t('common:optional')}
                            value={customerInfo.additionalInfo}
                            onChange={(e) => setCustomerInfo({ ...customerInfo, additionalInfo: e.target.value })}
                        />
                        <br />
                        <div style={{ marginBottom: '20px', marginTop: '0px' }}>
                            <input type="checkbox" checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} required />
                            <span> {t('publicPage:agreeToTerms')} 
                                {" "}
                                <a href={`${CONFIG.FRONTEND_URL}/privacy`} target="_blank" rel="noopener noreferrer">
                                    {t('common:privacyPolicy')}
                                </a>
                                {" "}{t('publicPage:and')}{" "}
                                <a href={`${CONFIG.FRONTEND_URL}/terms`} target="_blank" rel="noopener noreferrer">
                                    {t('common:termsOfService')}
                                </a>.
                            </span>
                        </div>
                        {error && <p style={styles.error}>{error}</p>}
                        {successMessage && <p style={styles.success}>{successMessage}</p>}

                        {sentVerificationCode && (
                        <>
                            <label style={styles.label}>{t('publicPage:verificationCode')}</label>
                            <input
                            type="text"
                            style={styles.input}
                            value={enteredCode}
                            onChange={(e) => setEnteredCode(e.target.value)}
                            />
                        </>
                        )}

                        {!sentVerificationCode && (
                            <button
                                style={{ 
                                    ...styles.button, 
                                    opacity: isButtonDisabled ? 0.5 : 1, 
                                    cursor: isButtonDisabled ? "not-allowed" : "pointer" 
                                }} 
                                onClick={handleSendCode}
                                disabled={isButtonDisabled}
                            >
                                {isButtonDisabled ? `${t('common:submit')} (${countdown}s)` : t('common:submit')}
                            </button>
                        )}

                        {sentVerificationCode && (
                        <div>
                        <button 
                            style={{ 
                                ...styles.button, 
                                opacity: isSubmitting ? 0.5 : 1, 
                                cursor: isSubmitting ? "not-allowed" : "pointer" 
                            }} 
                            onClick={handleBookingSubmit} //
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? t('publicPage:submitting') : t('publicPage:submitRequest')}
                        </button>

                        <button 
                                style={{ 
                                    ...styles.button, 
                                    opacity: isButtonDisabled ? 0.5 : 1, 
                                    cursor: isButtonDisabled ? "not-allowed" : "pointer" ,
                                    marginLeft: "50px",
                                    backgroundColor: "grey"
                                }} 
                                onClick={handleSendCode}
                                disabled={isButtonDisabled} // Disable the button while the countdown is active
                            >
                                {isButtonDisabled ? `${t('publicPage:requestCodeAgain')} (${countdown}s)` : t('publicPage:requestCodeAgain')}
                            </button>
                        </div>
                        )}
                        
                        
                    </div>
                )}
            </div>
            <div style={styles.footer}>
                <p>{t('common:poweredBy')} 
                {" "}
                <a href={`${CONFIG.FRONTEND_URL}/`} target="_blank" rel="noopener noreferrer">{t('common:appName')}</a>
                {" "}|{" "}
                <a href={`${CONFIG.FRONTEND_URL}/privacy`} target="_blank" rel="noopener noreferrer">{t('common:privacyPolicy')}</a>
                {" "}|{" "}
                <a href={`${CONFIG.FRONTEND_URL}/terms`} target="_blank" rel="noopener noreferrer">{t('common:termsOfService')}</a>
                </p>
            </div>
        </div>
    );
};

const styles = {
    container: { padding: "20px", maxWidth: "800px", margin: "0 auto" },
    title: { fontSize: "32px", marginBottom: "20px" },
    description: { 
        fontSize: "16px", 
        marginBottom: "20px",
        whiteSpace: "pre-line",  // Preserves new lines
        textAlign: "justify"      // Justifies text
    },    
    calendarSection: { marginBottom: "30px" },
    bookingSection: { marginTop: "20px" },
    label: { display: "block", marginBottom: "5px", fontWeight: "bold" },
    input: { display: "block", width: "80%", padding: "10px", marginBottom: "10px", borderRadius: "5px", border: "1px solid #ccc" },
    button: { 
        padding: "10px 20px", 
        backgroundColor: "#2c3e50", 
        color: "#fff", 
        border: "none", 
        borderRadius: "5px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        '&:hover': {
            backgroundColor: "#1a252f",
            transform: "translateY(-2px)",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)"
        }
    },
    error: { color: "red", marginTop: "10px" },
    success: { color: "green", marginTop: "10px" },
    colorBlock: {
        display: "inline-block",
        width: "40px",
        height: "15px", 
        backgroundColor: "rgba(17, 129, 0, 0.338)",
        borderRadius: "3px",
        border: "",
        margin: "0 5px"
    },
    startPrompt: { 
        display: "block", 
        marginTop: "60px", 
        marginBottom: "30px",
        fontWeight: "bold" 
    },
    sectionTitle: {
        fontSize: '24px',
        marginBottom: '20px',
        color: '#2c3e50',
    },
    servicesList: {
        display: 'grid',
        gap: '20px',
        marginBottom: '30px',
    },
    serviceCard: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '10px',
        boxShadow: '0 0 4px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
            backgroundColor: '#f8f9fa',
        }
    },
    serviceHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
    },
    serviceInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    serviceName: {
        margin: 0,
        fontWeight: 'bold',
        color: '#2c3e50',
    },
    serviceDuration: {
        fontSize: '14px',
        color: '#666',
        backgroundColor: '#f0f0f0',
        padding: '4px 12px',
        borderRadius: '12px',
        marginLeft: 'auto',
    },
    serviceDescription: {
        marginTop: '10px',
        marginBottom: '0',
        fontSize: '15px',
        color: '#555',
        lineHeight: '1.6',
    },
    footer: {
        marginTop: "50px",
        padding: "0px",
        backgroundColor: "rgb(226, 218, 195)",
        textAlign: "center",
        width: "100%",  
        display: "flex",
        justifyContent: "center",
        fontSize: "12px",
        height: "40px"
    }
    
};
const customSelectStyles = {
    control: (base) => ({
        ...base,
        padding: "5px",
        borderRadius: "5px",
        border: "1px solid #ccc",
        fontSize: "16px"
    })
};

export default PublicPage;
